/**
 * **Wie die Toasts übereinander liegen** (#223) — rein, ohne DOM, aus dem Prototyp
 * `tools/toast-layout-prototype` (Variante F) übernommen.
 *
 * Als **Stapel** liegt der neueste vorn, jeder ältere `offset` px weiter von der Kante weg und je
 * Ebene `shrink` kleiner dahinter, auf die Höhe des vordersten gekürzt (sein Inhalt ist dann
 * ausgeblendet). Ab `maxVisible` verschwinden ältere ganz. **Aufgefächert** (beim Überfahren)
 * steht jeder in voller Höhe neben dem nächsten, mit `gap` dazwischen.
 *
 * Richtungsfrei: `y` ist der Abstand von der Kante, an der der Stapel hängt (`AppToaster`: unten
 * rechts); der Aufrufer legt fest, ob das nach oben oder unten zählt.
 */

export interface ToastStackOptions {
  expanded: boolean
  /** So weit schaut ein älterer Toast oben heraus (px). */
  offset: number
  /** So viel kleiner je Ebene (0..1). */
  shrink: number
  /** Lücke zwischen aufgefächerten Toasts (px). */
  gap: number
  /** So viele Ebenen zeigt der Stapel; ältere werden ausgeblendet. */
  maxVisible: number
}

export interface ToastPose {
  /** Abstand von der Kante, an der der Stapel hängt (px, ≥ 0). */
  y: number
  scale: number
  height: number
  opacity: number
  /** Liegt hinter einem anderen — sein Inhalt wird ausgeblendet. */
  behind: boolean
}

/** Die Werte aus dem Prototyp (Sven, 04.10.2026). */
export const TOAST_STACK = { offset: 12, shrink: 0.05, gap: 10, maxVisible: 3 } as const

const round = (n: number) => Math.round(n * 10000) / 10000

/** `heights[0]` ist der neueste Toast. Liefert die Lage je Toast und die Höhe des Ganzen. */
export function toastStackLayout(
  heights: readonly number[],
  { expanded, offset, shrink, gap, maxVisible }: ToastStackOptions,
): { items: ToastPose[]; total: number } {
  if (heights.length === 0) return { items: [], total: 0 }
  const front = heights[0]!

  if (expanded) {
    let acc = 0
    const items = heights.map(height => {
      const pose = { y: acc, scale: 1, height, opacity: 1, behind: false }
      acc += height + gap
      return pose
    })
    return { items, total: acc - gap }
  }

  const items = heights.map((height, i) => ({
    y: offset * i,
    scale: round(1 - shrink * i),
    height: i === 0 ? height : front,
    opacity: i < maxVisible ? round(1 - i * 0.12) : 0,
    behind: i > 0,
  }))
  return { items, total: front + offset * (Math.min(heights.length, maxVisible) - 1) }
}
